interface MenuItem {
    id: string;
    pid?: string;
    name: string;
    path: string;
    rawPath?: string;
    component?: string;
    IsButton?: boolean;
    IsHide?: boolean;
    meta?: Record<string, any>;
    children?: MenuItem[];
    iconCls?: string;
    order?: number;
}

const viewModules = require.context('../views', true, /\.vue$/);
const viewModuleKeys = new Set(viewModules.keys());

const legacyRouteComponentKeys: Record<string, string> = {
    '/AI应用/AI设备读数': './visualModel/visualImg/index.vue',
    '/原始数据管理/原始检测数据列表': './RawDataManagement/OriginalDetectionList/index.vue',
    // '/原始数据管理/原始检测数据列表/原始检测数据详情': './RawDataManagement/OriginalDetectionList/details.vue',
    '/原始数据管理/经营项目数据分析': './RawDataManagement/AnalysisOfBusiness/index.vue',
    '/原始数据管理/原始数据变更记录': './RawDataManagement/ChangeRecords/index.vue',
    '/现场检测设置/检测项目定义': './ParameterSettings/tableEditing/index.vue',
    '/现场检测设置/经营项目定义': './ParameterSettings/BusinessScope/index.vue',
    '/现场检测设置/检测项目包': './ParameterSettings/TestItemPackage/index.vue',
    '/房屋建筑定期体检/体检方案定义': './HomeInspection/HouseCheckDefinition/index.vue',
    '/房屋建筑定期体检/体检项目定义': './HomeInspection/HouseCheckPlanDefinition/index.vue',
    '/权限管理/权限分配': './QuanXianGuanLi/fenpei.vue',
    '/权限管理/接口管理': './QuanXianGuanLi/jiekou.vue',
    '/权限管理/菜单管理': './QuanXianGuanLi/caidan.vue',
    '/权限管理/角色管理': './QuanXianGuanLi/juese.vue',
    '/开发专用/App版本管理': './KaiFa/AppVersionAdmin/index.vue',
    '/开发专用/系统监控': './KaiFa/SystemMonitor/index.vue',
};

const emptyRouteValues = new Set(['', '-', '无']);

const getValue = (...values: any[]) => {
    return values.find(value => value !== undefined && value !== null);
};

const normalizeSlash = (value: string) => value.replace(/\\/g, '/').replace(/\/+/g, '/');

const trimSlashes = (value: string) => normalizeSlash(value).replace(/^\/+|\/+$/g, '');

const isEmptyRouteValue = (value: unknown) => {
    if (value === undefined || value === null) return true;
    return emptyRouteValues.has(String(value).trim());
};

const toDisplayPath = (parentPath: string, name: string) => {
    const currentName = String(name || '').trim();
    if (!currentName) return parentPath || '';
    const parent = trimSlashes(parentPath);
    return `/${[parent, currentName].filter(Boolean).join('/')}`;
};

const looksLikeComponentPath = (value: string) => {
    const normalized = normalizeSlash(value).trim();
    return /\.vue$/i.test(normalized)
        || normalized.startsWith('@/views/')
        || normalized.startsWith('src/views/')
        || normalized.startsWith('/src/views/')
        || normalized.startsWith('views/')
        || normalized.startsWith('/views/');
};

const normalizeComponentKey = (value: string) => {
    let key = normalizeSlash(value).trim();
    key = key
        .replace(/^\.\//, '')
        .replace(/^@\/views\//, '')
        .replace(/^\/?src\/views\//, '')
        .replace(/^\/?views\//, '')
        .replace(/^\/+/, '');

    if (!key) return '';
    if (!/\.vue$/i.test(key)) {
        key = key.endsWith('/') ? `${key}index.vue` : `${key}/index.vue`;
    }
    return `./${key}`;
};

const findComponentKey = (componentPath: string, routePath = '') => {
    if (!isEmptyRouteValue(componentPath)) {
        const componentKey = normalizeComponentKey(componentPath);
        if (viewModuleKeys.has(componentKey)) return componentKey;
    }

    const legacyKey = legacyRouteComponentKeys[routePath];
    if (legacyKey && viewModuleKeys.has(legacyKey)) return legacyKey;

    return '';
};

const childRouteComponentKeys: Record<string, Array<{ segment: string; title: string; component: string; noCache?: boolean }>> = {
    './RawDataManagement/OriginalDetectionList/index.vue': [
        {
            segment: '原始检测数据详情',
            title: '原始记录详情',
            component: './RawDataManagement/OriginalDetectionList/details.vue',
        },
    ],
    './RawDataManagement/AnalysisOfBusiness/index.vue': [
        {
            segment: '原始记录打印',
            title: '原始记录打印',
            component: './RawDataManagement/AnalysisOfBusiness/printList.vue',
            noCache: true,
        },
        {
            segment: '原始数据汇总',
            title: '原始数据汇总',
            component: './RawDataManagement/AnalysisOfBusiness/summary.vue',
            noCache: true,
        },
    ],
    './ParameterSettings/tableEditing/index.vue': [
        {
            segment: '打印列表',
            title: '打印列表',
            component: './ParameterSettings/tableEditing/printList.vue',
            noCache: true,
        },
    ],
    './HomeInspection/HouseCheckPlanDefinition/index.vue': [
        {
            segment: '体检记录详情',
            title: '体检记录详情',
            component: './HomeInspection/HouseCheckPlanDefinition/recordDetails.vue',
            noCache: true,
        },
    ],
};

const createComponentImport = (componentPath: string, routePath: string) => {
    const componentKey = findComponentKey(componentPath, routePath);
    if (!componentKey) {
        console.warn(`未找到菜单对应组件: route=${routePath}, component=${componentPath || '-'}`);
        return () => import('@/views/error/NotFound.vue');
    }

    return () => {
        const module = viewModules(componentKey);
        return Promise.resolve(module.default || module);
    };
};

const normalizeMenuItem = (item: any, parentPath = ''): MenuItem => {
    const name = String(getValue(item.meta?.title, item.name, item.Name, '')).trim();
    const rawPath = String(getValue(
        item.component,
        item.Component,
        item.componentPath,
        item.ComponentPath,
        item.Code,
        item.path,
        ''
    )).trim();
    const isComponentPath = !isEmptyRouteValue(rawPath) && looksLikeComponentPath(rawPath);
    // 首页是前端内置静态路由，不能按菜单名称生成“/首页”动态路径。
    const path = name === '首页' ? '/home' : toDisplayPath(parentPath, name);
    const children = getValue(item.children, item.Children, []);
    const icon = getValue(item.meta?.icon, item.iconCls, item.IconNew, item.Icon, '');

    return {
        ...item,
        id: String(getValue(item.id, item.Id, '')),
        pid: String(getValue(item.pid, item.Pid, '')),
        name,
        path,
        rawPath,
        component: isComponentPath ? rawPath : String(getValue(item.component, item.Component, '')),
        IsButton: Boolean(getValue(item.IsButton, false)),
        IsHide: Boolean(getValue(item.IsHide, false)),
        iconCls: icon,
        order: Number(getValue(item.order, item.OrderSort, 0)) || 0,
        meta: {
            ...(item.meta || {}),
            title: name,
            icon,
            keepAlive: getValue(item.meta?.keepAlive, item.IskeepAlive),
        },
        children: Array.isArray(children)
            ? children.map((child: any) => normalizeMenuItem(child, path))
            : [],
    };
};

const shouldRegisterRoute = (item: MenuItem) => {
    if (item.IsButton || item.IsHide) return false;
    if (!item.path || isEmptyRouteValue(item.path)) return false;
    if (item.path === '/' || item.path === '/home') return false;
    return Boolean(findComponentKey(item.component || item.rawPath || '', item.path));
};

const normalizeSidebarChildren = (menus: MenuItem[]): MenuItem[] => {
    return menus
        .filter(menu => !menu.IsHide && !menu.IsButton)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(menu => ({
            ...menu,
            children: normalizeSidebarChildren(menu.children || []),
        }));
};

// 处理菜单数据，生成侧边栏需要的中文访问路径。
export function processMenusForSidebar(menus: any[]): MenuItem[] {
    if (!Array.isArray(menus)) {
        menus = [];
    }

    const sidebarMenus = normalizeSidebarChildren(menus.map(menu => normalizeMenuItem(menu)));
    const hasHomeMenu = sidebarMenus.some(menu => menu.path === '/home');

    if (!hasHomeMenu) {
        sidebarMenus.unshift({
            id: '__builtin_home__',
            name: '首页',
            path: '/home',
            IsButton: false,
            IsHide: false,
            iconCls: 'fa-home',
            order: -Infinity,
            meta: {
                title: '首页',
                icon: 'HomeFilled',
            },
            children: [],
        });
    }

    return sidebarMenus;
}

export default function addDynamicRoutes(router: any, menus: any[]) {
    const allRoutes = router.options.routes;
    const layoutRoute = allRoutes.find((route: any) => route.name == 'Layout');
    if (!layoutRoute || !Array.isArray(menus)) {
        return;
    }

    const existingPaths = new Set<string>();
    router.getRoutes().forEach((route: any) => {
        existingPaths.add(route.path);
    });

    let addedCount = 0;
    const processedMenus = menus.map(menu => normalizeMenuItem(menu));

    const registerRoute = (
        path: string,
        title: string,
        componentPath: string,
        meta: Record<string, any> = {}
    ) => {
        if (!path || existingPaths.has(path)) {
            const existingRoute = router.getRoutes().find((route: any) => route.path === path);
            if (existingRoute?.meta) {
                existingRoute.meta.title = title;
            }
            return false;
        }

        router.addRoute(layoutRoute.name, {
            path,
            name: path.replace(/\//g, '_').substring(1) || 'home',
            meta: {
                title,
                requireAuth: true,
                ...meta,
            },
            component: createComponentImport(componentPath, path),
        });

        existingPaths.add(path);
        addedCount++;
        return true;
    };

    const registerChildRoutes = (item: MenuItem, parentComponentKey: string) => {
        const childRoutes = childRouteComponentKeys[parentComponentKey];
        if (!childRoutes || childRoutes.length === 0) return;

        childRoutes.forEach((child) => {
            const childPath = toDisplayPath(item.path, child.segment);
            registerRoute(childPath, child.title, child.component, {
                icon: item.meta?.icon || item.iconCls,
                noCache: child.noCache,
            });
        });
    };

    function processMenuItems(menuItems: MenuItem[]) {
        if (!Array.isArray(menuItems)) return;

        menuItems.forEach((item: MenuItem) => {
            if (!item.IsButton && shouldRegisterRoute(item)) {
                const componentPath = item.component || item.rawPath || '';
                const componentKey = findComponentKey(componentPath, item.path);
                if (existingPaths.has(item.path)) {
                    const existingRoute = router.getRoutes().find((route: any) => route.path === item.path);
                    if (existingRoute?.meta) {
                        existingRoute.meta.title = item.meta?.title || item.name;
                        existingRoute.meta.icon = item.meta?.icon || item.iconCls;
                    }
                } else {
                    registerRoute(item.path, item.meta?.title || item.name, componentPath, {
                        icon: item.meta?.icon || item.iconCls,
                        requireAuth: item.meta?.requireAuth !== false,
                        keepAlive: item.meta?.keepAlive,
                    });
                }
                registerChildRoutes(item, componentKey);
            }

            if (item.children && item.children.length > 0) {
                processMenuItems(item.children);
            }
        });
    }

    processMenuItems(processedMenus);
    console.log(`动态路由添加完成，本次新增 ${addedCount} 个路由`);
}
